import type { Folder, Project } from '../services/folder.service';

/**
 * Recursively find a folder by id in the tree.
 */
export function findFolder(items: Folder[], id: number): Folder | undefined {
  for (const f of items) {
    if (f.folderId === id) {
      return f;
    }
    const child = f.folders && findFolder(f.folders, id);
    if (child) {
      return child;
    }
  }
  return undefined;
}

/**
 * Recursively find a project by id in the tree.
 */
export function findProject(items: Folder[], id: number): Project | undefined {
  for (const f of items) {
    const proj = f.projects.find((p) => p.testProjectID === id);
    if (proj) {
      return proj;
    }
    const child = f.folders && findProject(f.folders, id);
    if (child) {
      return child;
    }
  }
  return undefined;
}

/**
 * Recursively find the parent folder ID for a given project in the tree.
 */
export function findProjectParent(items: Folder[], projectId: number): number | null {
  for (const f of items) {
    if (f.projects.some((p) => p.testProjectID === projectId)) {
      return f.folderId;
    }
    const childParent = findProjectParent(f.folders, projectId);
    if (childParent !== null) {
      return childParent;
    }
  }
  return null;
}

/**
 * Recursively collect all project IDs present within a folder structure.
 */
export function getProjectIdsInFolders(items: Folder[]): Set<number> {
  const projectIds = new Set<number>();

  function traverse(folders: Folder[]) {
    for (const f of folders) {
      f.projects.forEach((p) => projectIds.add(p.testProjectID));
      if (f.folders && f.folders.length > 0) {
        traverse(f.folders);
      }
    }
  }

  traverse(items);
  return projectIds;
}

/**
 * Find the path of folder IDs from the root to the target folder.
 * Returns an array like [rootChildId, ..., targetFolderId] or null if not found.
 */
export function findFolderPath(
  items: Folder[],
  targetId: number,
  path: number[] = []
): number[] | null {
  for (const folder of items) {
    const newPath = [...path, folder.folderId];
    if (folder.folderId === targetId) {
      return newPath;
    }
    const childPath = folder.folders && findFolderPath(folder.folders, targetId, newPath);
    if (childPath) {
      return childPath;
    }
  }
  return null;
}

/**
 * Find the folder path that contains the given project ID.
 * Returns an array of folder IDs from root to the parent folder or null.
 */
export function findProjectFolderPath(
  items: Folder[],
  projectId: number,
  path: number[] = []
): number[] | null {
  for (const folder of items) {
    const newPath = [...path, folder.folderId];
    if (folder.projects.some((p) => p.testProjectID === projectId)) {
      return newPath;
    }
    const childPath = folder.folders && findProjectFolderPath(folder.folders, projectId, newPath);
    if (childPath) {
      return childPath;
    }
  }
  return null;
}
